import React from 'react';
import {
  Box,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Typography,
  Paper,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import {
  ASSIGNABLE_USERS_QUERY_KEY,
  asAssignableUsers,
  fetchAssignableUsers,
} from '../../lib/assignableUsers';
import { getAuthToken } from '../../lib/authToken';

interface Filters {
  search: string;
  status: string;
  priority: string;
  taskType: string;
  assignedTo: string;
  caseId: string;
  clientId: string;
  dueDateFrom: string;
  dueDateTo: string;
  isOverdue: boolean;
}

interface TaskFiltersProps {
  filters: Filters;
  onFilterChange: (newFilters: Partial<Filters>) => void;
}

const TaskFilters: React.FC<TaskFiltersProps> = ({ filters, onFilterChange }) => {
  // Fetch users for assignment filter (assignable list — not Team Management)
  const { data: usersData } = useQuery({
    queryKey: ASSIGNABLE_USERS_QUERY_KEY,
    queryFn: fetchAssignableUsers,
  });
  const users = asAssignableUsers(usersData);

  // Fetch cases for case filter
  const { data: casesData } = useQuery({
    queryKey: ['cases'],
    queryFn: async () => {
      const response = await fetch('/api/cases', {
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`,
        },
      });
      if (!response.ok) throw new Error('Failed to fetch cases');
      return response.json();
    },
  });

  // Fetch clients for client filter
  const { data: clientsData } = useQuery({
    queryKey: ['clients'],
    queryFn: async () => {
      const response = await fetch('/api/clients', {
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`,
        },
      });
      if (!response.ok) throw new Error('Failed to fetch clients');
      return response.json();
    },
  });

  const statusOptions = [
    { value: 'pending', label: 'Pending' },
    { value: 'in_progress', label: 'In Progress' },
    { value: 'completed', label: 'Completed' },
    { value: 'cancelled', label: 'Cancelled' },
    { value: 'on_hold', label: 'On Hold' },
  ];

  const priorityOptions = [
    { value: 'low', label: 'Low' },
    { value: 'medium', label: 'Medium' },
    { value: 'high', label: 'High' },
    { value: 'urgent', label: 'Urgent' },
  ];

  const taskTypeOptions = [
    { value: 'client_meeting', label: 'Client Meeting' },
    { value: 'court_hearing', label: 'Court Hearing' },
    { value: 'document_review', label: 'Document Review' },
    { value: 'research', label: 'Research' },
    { value: 'filing_deadline', label: 'Filing Deadline' },
    { value: 'follow_up', label: 'Follow Up' },
    { value: 'billing', label: 'Billing' },
    { value: 'administrative', label: 'Administrative' },
    { value: 'case_preparation', label: 'Case Preparation' },
    { value: 'discovery', label: 'Discovery' },
    { value: 'settlement_negotiation', label: 'Settlement Negotiation' },
    { value: 'trial_preparation', label: 'Trial Preparation' },
    { value: 'other', label: 'Other' },
  ];

  const cases = casesData?.data?.cases || [];
  const clients = clientsData?.data?.clients || [];

  return (
    <Paper sx={{ p: 2, mb: 2 }}>
      <Typography variant="h6" gutterBottom>
        Filters
      </Typography>
      
      <Grid container spacing={2}>
        <Grid item xs={12} md={3}>
          <FormControl fullWidth size="small">
            <InputLabel>Status</InputLabel>
            <Select
              value={filters.status}
              onChange={(e) => onFilterChange({ status: e.target.value })}
              label="Status"
            >
              <MenuItem value="">
                <em>All Statuses</em>
              </MenuItem>
              {statusOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={3}>
          <FormControl fullWidth size="small">
            <InputLabel>Priority</InputLabel>
            <Select
              value={filters.priority}
              onChange={(e) => onFilterChange({ priority: e.target.value })}
              label="Priority"
            >
              <MenuItem value="">
                <em>All Priorities</em>
              </MenuItem>
              {priorityOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={3}>
          <FormControl fullWidth size="small">
            <InputLabel>Task Type</InputLabel>
            <Select
              value={filters.taskType}
              onChange={(e) => onFilterChange({ taskType: e.target.value })}
              label="Task Type"
            >
              <MenuItem value="">
                <em>All Types</em>
              </MenuItem>
              {taskTypeOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={3}>
          <FormControl fullWidth size="small">
            <InputLabel>Assigned To</InputLabel>
            <Select
              value={filters.assignedTo}
              onChange={(e) => onFilterChange({ assignedTo: e.target.value })}
              label="Assigned To"
            >
              <MenuItem value="">
                <em>All Users</em>
              </MenuItem>
              {users.map((user: any) => (
                <MenuItem key={user.id} value={user.id}>
                  {user.firstName} {user.lastName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={3}>
          <FormControl fullWidth size="small">
            <InputLabel>Case</InputLabel>
            <Select
              value={filters.caseId}
              onChange={(e) => onFilterChange({ caseId: e.target.value })}
              label="Case"
            >
              <MenuItem value="">
                <em>All Cases</em>
              </MenuItem>
              {cases.map((caseItem: any) => (
                <MenuItem key={caseItem.id} value={caseItem.id}>
                  {caseItem.caseNumber} - {caseItem.title}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={3}>
          <FormControl fullWidth size="small">
            <InputLabel>Client</InputLabel>
            <Select
              value={filters.clientId}
              onChange={(e) => onFilterChange({ clientId: e.target.value })}
              label="Client"
            >
              <MenuItem value="">
                <em>All Clients</em>
              </MenuItem>
              {clients.map((client: any) => (
                <MenuItem key={client.id} value={client.id}>
                  {client.firstName} {client.lastName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={3}>
          <TextField
            fullWidth
            size="small"
            label="Due Date From"
            type="date"
            value={filters.dueDateFrom}
            onChange={(e) => onFilterChange({ dueDateFrom: e.target.value })}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>

        <Grid item xs={12} md={3}>
          <TextField
            fullWidth
            size="small"
            label="Due Date To"
            type="date"
            value={filters.dueDateTo}
            onChange={(e) => onFilterChange({ dueDateTo: e.target.value })}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>

        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                checked={filters.isOverdue}
                onChange={(e) => onFilterChange({ isOverdue: e.target.checked })}
              />
            }
            label="Show Overdue Tasks Only"
          />
        </Grid>
      </Grid>
    </Paper>
  );
};

export default TaskFilters; 